import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerClient, getClientsByRut, getClientPricingCategories } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import ClientPricingDiscountConfigurator from '../components/ClientPricingDiscountConfigurator';

const ClientRegistrationPage = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState({
    name: '',
    rut: '',
    email: '',
    address: '',
    city: '',
    phone: '',
    giro: '',
    creditLimit: '',
    creditBlocked: false,
    creditWarningNote: '',
    clientPricingCategoryIds: [],
  });
  const [pricingCategories, setPricingCategories] = useState([]);
  const [pricingCategoriesLoading, setPricingCategoriesLoading] = useState(false);
  const [clientPricingMode, setClientPricingMode] = useState('none');
  const [customPricingDiscounts, setCustomPricingDiscounts] = useState([]);
  const [clientPricingEditorReady, setClientPricingEditorReady] = useState(false);
  const [clientPricingDraft, setClientPricingDraft] = useState(null);
  const [saveCustomPricingAsCategory, setSaveCustomPricingAsCategory] = useState(false);
  const [customPricingCategoryName, setCustomPricingCategoryName] = useState('');
  const [customPricingCategoryDescription, setCustomPricingCategoryDescription] = useState('');
  const [error, setError] = useState('');
  const { user } = useCurrentUser();
  const canCreateClient = hasPermission(user, 'client.create');
  const canReadClient = hasPermission(user, 'client.read');
  const canManageCredit = canCreateClient && hasPermission(user, 'branch.access.any');
  const canManageClientPricing = hasPermission(user, 'client_pricing.manage');

  useEffect(() => {
    if (!canManageClientPricing) return;
    let mounted = true;
    const loadPricingCategories = async () => {
      setPricingCategoriesLoading(true);
      try {
        const data = await getClientPricingCategories();
        if (!mounted) return;
        setPricingCategories(Array.isArray(data?.categories) ? data.categories : []);
      } catch (err) {
        if (mounted) setPricingCategories([]);
      } finally {
        if (mounted) setPricingCategoriesLoading(false);
      }
    };
    loadPricingCategories();
    return () => {
      mounted = false;
    };
  }, [canManageClientPricing]);

  const formatRut = (e) => {
    setClient({ ...client, rut: e.target.value.replace(/[^0-9kK]/g, '').replace(/^(\d{1,2})(\d{3})(\d{3})([0-9kK])$/, '$1.$2.$3-$4').toUpperCase() });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClient({ ...client, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateClient) return;
    setError('');
    try {
      if (canReadClient) {
        const clients = await getClientsByRut(client.rut);
        const existingClient = clients.pop();
        if (existingClient) {
          navigate(`/client`);
          return;
        }
      }
      const payload = { ...client };
      if (canManageCredit) {
        const trimmedLimit = String(client.creditLimit ?? '').trim();
        if (trimmedLimit === '') {
          payload.creditLimit = null;
        } else {
          const parsedLimit = Number(trimmedLimit);
          if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
            setError('El limite de credito debe ser un numero valido.');
            return;
          }
          payload.creditLimit = parsedLimit;
        }
        payload.creditBlocked = Boolean(client.creditBlocked);
        payload.creditWarningNote = client.creditWarningNote?.trim() || null;
      } else {
        delete payload.creditLimit;
        delete payload.creditBlocked;
        delete payload.creditWarningNote;
      }
      if (canManageClientPricing) {
        if (!clientPricingEditorReady) {
          setError('Espera a que terminen de cargar las reglas de precios personalizadas.');
          return;
        }
        if (clientPricingMode === 'category' && !client.clientPricingCategoryIds?.length) {
          setError('Selecciona una categoria de descuento.');
          return;
        }
        if (clientPricingMode === 'custom' && saveCustomPricingAsCategory && !customPricingCategoryName.trim()) {
          setError('Ingresa un nombre para crear la categoria de descuento.');
          return;
        }
        payload.clientPricingMode = clientPricingMode;
        payload.clientPricingCategoryIds = clientPricingMode === 'category'
          ? client.clientPricingCategoryIds || []
          : [];
        payload.customPricingDiscounts = clientPricingMode === 'custom' ? customPricingDiscounts : [];
        if (clientPricingDraft && clientPricingMode === 'custom') {
          payload.clientPricingDraftId = clientPricingDraft.id;
          payload.clientPricingDraftRevision = clientPricingDraft.revision;
        }
        payload.saveCustomPricingAsCategory = clientPricingMode === 'custom' && saveCustomPricingAsCategory;
        payload.customPricingCategoryName = customPricingCategoryName.trim();
        payload.customPricingCategoryDescription = customPricingCategoryDescription.trim();
      } else {
        delete payload.clientPricingCategoryId;
        delete payload.clientPricingCategoryIds;
      }
      await registerClient(payload);
      navigate('/client');
    } catch (error) {
      console.error('Error creating client:', error);
      setError('No se pudo registrar el cliente.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-24 px-4 pb-10">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
          <p className="text-xs font-semibold uppercase text-gray-500">Clientes</p>
          <h1 className="text-2xl font-bold text-gray-900">Registrar cliente</h1>
          <p className="mt-1 text-sm text-gray-600">
            Crea el perfil comercial, condiciones de credito y categoria de descuento.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {error && <p className="rounded border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}

          <section className="space-y-4">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Datos comerciales</h2>
              <p className="text-xs text-gray-500">Informacion usada en ventas, cotizaciones y documentos.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">RUT</label>
                <input name="rut" placeholder="RUT" value={client.rut} onChange={formatRut} required className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">Nombre</label>
                <input name="name" placeholder="Nombre" value={client.name} onChange={handleChange} required className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">Email</label>
                <input name="email" type="email" placeholder="Email" value={client.email} onChange={handleChange} required className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">Giro</label>
                <input name="giro" placeholder="Giro" value={client.giro} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs font-semibold uppercase text-gray-600">Direccion</label>
                <input name="address" placeholder="Direccion" value={client.address} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">Ciudad</label>
                <input name="city" placeholder="Ciudad" value={client.city} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-gray-600">Telefono</label>
                <input name="phone" placeholder="Telefono" value={client.phone} onChange={handleChange} className="mt-2 w-full rounded-lg border border-gray-300 p-3" disabled={!canCreateClient} />
              </div>
            </div>
          </section>

          {canManageClientPricing && (
            <ClientPricingDiscountConfigurator
              mode={clientPricingMode}
              onModeChange={setClientPricingMode}
              categoryValues={client.clientPricingCategoryIds || []}
              onCategoriesChange={(ids) => setClient((current) => ({ ...current, clientPricingCategoryIds: ids }))}
              categories={pricingCategories}
              categoriesLoading={pricingCategoriesLoading}
              customDiscounts={customPricingDiscounts}
              onCustomDiscountsChange={setCustomPricingDiscounts}
              saveAsCategory={saveCustomPricingAsCategory}
              onSaveAsCategoryChange={setSaveCustomPricingAsCategory}
              categoryName={customPricingCategoryName}
              onCategoryNameChange={setCustomPricingCategoryName}
              categoryDescription={customPricingCategoryDescription}
              onCategoryDescriptionChange={setCustomPricingCategoryDescription}
              onReadyChange={setClientPricingEditorReady}
              onDraftChange={setClientPricingDraft}
              currentUser={user}
              disabled={!canCreateClient}
            />
          )}

          {canManageCredit && (
            <section className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-800">Credito</h2>
                <p className="text-xs text-gray-500">Controla limite y bloqueos comerciales del cliente.</p>
              </div>
              <input
                type="number"
                min="0"
                name="creditLimit"
                placeholder="Limite de credito (opcional)"
                value={client.creditLimit}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white p-3"
                disabled={!canCreateClient}
              />
              <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  name="creditBlocked"
                  checked={client.creditBlocked}
                  onChange={handleChange}
                  disabled={!canCreateClient}
                />
                Bloquear credito para este cliente
              </label>
              <textarea
                name="creditWarningNote"
                placeholder="Nota de advertencia"
                value={client.creditWarningNote}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 bg-white p-3"
                rows={3}
                disabled={!canCreateClient}
              />
            </section>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-end">
            <button type="button" className="rounded-lg border border-gray-200 px-4 py-2 text-gray-700 hover:text-gray-900" onClick={() => navigate('/client')}>Cancelar</button>
            {canCreateClient && (
              <button
                type="submit"
                disabled={canManageClientPricing && clientPricingMode === 'custom' && !clientPricingEditorReady}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Registrar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientRegistrationPage;
